/**
 * Phase 27.31 — Lead Landing Pages admin management screen.
 *
 * Rendered at /leads/landing-pages. Displays the table of existing
 * pages with quick actions (view public, copy link, activate/deactivate,
 * edit, delete). "+ Create" opens the editor at /leads/landing-pages/new.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, ExternalLink, Copy, Edit, Trash2, Power, PowerOff, Loader2, Search, ShieldCheck,
  Calendar, CalendarOff,
} from 'lucide-react';
import { leadLandingPagesAPI } from '../lib/api';
import { LANDING_STATUSES } from '../lib/landingPageConstants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

function statusBadge(status) {
  const meta = LANDING_STATUSES.find((s) => s.key === status) || LANDING_STATUSES[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${meta.tone}`}>{meta.label}</span>;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return iso; }
}

export default function LeadLandingPagesPage({ mine = false }) {
  const navigate = useNavigate();
  const basePath = mine ? '/my-landing-page' : '/leads/landing-pages';
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = mine ? await leadLandingPagesAPI.mineList() : await leadLandingPagesAPI.list();
      setPages(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return pages;
    const t = q.toLowerCase();
    return pages.filter((p) =>
      (p.name || '').toLowerCase().includes(t)
      || (p.slug || '').toLowerCase().includes(t)
      || (p.assigned_agent_name || '').toLowerCase().includes(t),
    );
  }, [pages, q]);

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/connect/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch { toast.error('Could not copy — please copy manually'); }
  };

  const toggleStatus = async (page) => {
    const next = page.status === 'active' ? 'inactive' : 'active';
    setBusy(page.landing_page_id);
    try {
      await leadLandingPagesAPI.update(page.landing_page_id, { status: next });
      toast.success(`Landing page ${next === 'active' ? 'activated' : 'deactivated'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  const remove = async (page) => {
    if (!window.confirm(`Delete "${page.name}"? This cannot be undone.`)) return;
    setBusy(page.landing_page_id);
    try {
      await leadLandingPagesAPI.remove(page.landing_page_id);
      toast.success('Landing page deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(null); }
  };

  // Quick-enable/disable appointment booking without opening the
  // editor. When switching a legacy landing page that has no
  // `appointments_config` on it yet, seed sensible defaults
  // (weekdays 9-5 ET, 30-min slots) so the toggle is immediately
  // usable. Admin can fine-tune later in the editor.
  const toggleAppointments = async (page) => {
    setBusy(page.landing_page_id);
    try {
      const current = page.appointments_config || {};
      const next = {
        enabled: !current.enabled,
        duration_minutes: current.duration_minutes || 30,
        timezone: current.timezone || 'America/New_York',
        available_windows: (current.available_windows && current.available_windows.length)
          ? current.available_windows
          : [
            { dow: 0, start_time: '09:00', end_time: '17:00' },
            { dow: 1, start_time: '09:00', end_time: '17:00' },
            { dow: 2, start_time: '09:00', end_time: '17:00' },
            { dow: 3, start_time: '09:00', end_time: '17:00' },
            { dow: 4, start_time: '09:00', end_time: '17:00' },
          ],
        min_notice_hours: current.min_notice_hours ?? 4,
        max_days_ahead: current.max_days_ahead ?? 30,
        buffer_minutes: current.buffer_minutes ?? 0,
      };
      await leadLandingPagesAPI.update(page.landing_page_id, { appointments_config: next });
      toast.success(
        next.enabled
          ? 'Appointments enabled — Mon–Fri 9-5 ET set by default. Fine-tune in the editor.'
          : 'Appointments disabled'
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="landing-pages-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{mine ? 'My Landing Page' : 'Lead Landing Pages'}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {mine
              ? 'Your personal public page for collecting new leads. Share the link with prospects.'
              : 'Create and manage public lead-generation pages for agents.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!mine && (
            <Button
              variant="outline"
              onClick={() => navigate('/leads/landing-pages/consent')}
              data-testid="landing-consent-settings"
            >
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Consent
            </Button>
          )}
          {(!mine || pages.length === 0) && (
            <Button
              onClick={() => navigate(`${basePath}/new`)}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="landing-page-create"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Create Landing Page
            </Button>
          )}
        </div>
      </div>

      {mine && pages.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="my-landing-extra-note">
          You already have a landing page. Need another page for a campaign or special project? Contact KR Admin.
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search name, slug, or agent…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="landing-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-2xl py-16 text-center bg-white">
          <p className="text-slate-500">No landing pages yet.</p>
          <Button className="mt-4" onClick={() => navigate(`${basePath}/new`)} data-testid="landing-empty-create">
            <Plus className="mr-1.5 h-4 w-4" /> Create your {mine ? 'landing page' : 'first landing page'}
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Assigned Agent</th>
                  <th className="text-left px-4 py-3">URL</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Views</th>
                  <th className="text-right px-4 py-3">Leads</th>
                  <th className="text-left px-4 py-3">Last Updated</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => (
                  <tr key={p.landing_page_id} data-testid={`landing-row-${p.landing_page_id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <Link to={`${basePath}/${p.landing_page_id}`} className="hover:underline">
                        {p.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.assigned_agent_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">/connect/{p.slug}</td>
                    <td className="px-4 py-3">{statusBadge(p.status)}</td>
                    <td className="px-4 py-3 text-right">{p.stats?.views || 0}</td>
                    <td className="px-4 py-3 text-right">{p.stats?.submissions || 0}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(p.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {p.status === 'active' && (
                          <a
                            href={`/connect/${p.slug}`} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                            title="View public page"
                            data-testid={`landing-view-${p.landing_page_id}`}
                          ><ExternalLink size={16} /></a>
                        )}
                        <button
                          onClick={() => copyLink(p.slug)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                          title="Copy link"
                          data-testid={`landing-copy-${p.landing_page_id}`}
                        ><Copy size={16} /></button>
                        <button
                          onClick={() => navigate(`${basePath}/${p.landing_page_id}`)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                          title="Edit"
                          data-testid={`landing-edit-${p.landing_page_id}`}
                        ><Edit size={16} /></button>
                        <button
                          onClick={() => toggleAppointments(p)}
                          disabled={busy === p.landing_page_id}
                          className={`p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 ${
                            p.appointments_config?.enabled
                              ? 'text-sky-600 hover:text-sky-700 bg-sky-50'
                              : 'text-slate-400 hover:text-slate-700'
                          }`}
                          title={p.appointments_config?.enabled
                            ? 'Appointments enabled — click to disable'
                            : 'Enable appointment booking'}
                          data-testid={`landing-appts-${p.landing_page_id}`}
                        >
                          {p.appointments_config?.enabled ? <Calendar size={16} /> : <CalendarOff size={16} />}
                        </button>
                        <button
                          onClick={() => toggleStatus(p)}
                          disabled={busy === p.landing_page_id}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 disabled:opacity-40"
                          title={p.status === 'active' ? 'Deactivate' : 'Activate'}
                          data-testid={`landing-toggle-${p.landing_page_id}`}
                        >
                          {p.status === 'active' ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        {!mine && (
                          <button
                            onClick={() => remove(p)}
                            disabled={busy === p.landing_page_id}
                            className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600 disabled:opacity-40"
                            title="Delete"
                            data-testid={`landing-delete-${p.landing_page_id}`}
                          ><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
